import { type Attachment } from 'src/api/generated';
import { isRecord } from 'src/modules/shared/utils/types.util';

/**
 * Returns the filename from a URL path. Useful fallback when metadata is missing.
 */
export const getFileNameFromUrl = (url: string): string => {
  try {
    const urlPath = new URL(url).pathname;
    const fileName = urlPath.split('/').pop();
    return fileName ?? 'document';
  } catch {
    return 'document';
  }
};

const getString = (value: unknown): string | undefined => {
  return typeof value === 'string' ? value : undefined;
};

/**
 * Extracts the filename from WhatsApp-like attachment metadata when available.
 */
export const getFileNameFromMetadata = (
  attachment: Attachment
): string | undefined => {
  const { metadata } = attachment;
  if (!isRecord(metadata)) return undefined;

  const message = isRecord(metadata.message) ? metadata.message : undefined;
  const document =
    message && isRecord(message.document) ? message.document : undefined;
  if (!document) return undefined;

  const filename =
    getString(document.filename) ??
    getString(document.file_name) ??
    getString(document.name);
  if (filename?.trim()) {
    return filename.trim();
  }

  return undefined;
};

/**
 * Best-effort filename resolver prioritizing metadata, with URL fallback.
 */
export const getBestFileName = (attachment: Attachment): string => {
  const fromMeta = getFileNameFromMetadata(attachment);
  if (fromMeta != null) return fromMeta;

  return getFileNameFromUrl(attachment.url);
};
